import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Eye, Users, Mail, MousePointerClick, LogOut, TrendingUp, Save, Download, Trash2, ImagePlus, MessageCircle, Archive, Type, Rocket, BarChart3, Music, Crown, Flag, Ban, EyeOff, Pin, Reply, X, Heart, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { logError } from "../lib/log";
import { useAuth } from "../context/AuthContext";
import { LOGO } from "../lib/site";
import { InstagramIcon, TikTokIcon, YouTubeIcon } from "../components/BrandIcons";

const GOLD = "#D4AF37";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Stable references for recharts props (avoids new object/array on every render)
const CHART_MARGIN = { top: 5, right: 5, left: -20, bottom: 0 };
const TICK_SM = { fill: "#8A8A8A", fontSize: 11 };
const TICK_MD = { fill: "#A3A3A3", fontSize: 12 };
const BAR_RADIUS = [2, 2, 0, 0];
const CARD_INITIAL = { opacity: 0, y: 20 };
const CARD_ANIMATE = { opacity: 1, y: 0 };
const TOOLTIP_CURSOR = { fill: "rgba(212,175,55,0.06)" };

const SITE_IMAGE_SLOTS = [
  { slot: "heroBg", label: "Fond du Hero (haut de page)", ratio: "16 / 9", pos: "center", crop: "Panoramique · en fond" },
  { slot: "heroMain", label: "Grande photo du Hero", ratio: "16 / 9", pos: "top", crop: "Paysage large · haut de l'image visible" },
  { slot: "quote", label: "Photo bannière citation", ratio: "21 / 9", pos: "center", crop: "Panoramique" },
  { slot: "journey", label: "Photo « Sur scène »", ratio: "16 / 10", pos: "center", crop: "Paysage" },
  { slot: "videoCover", label: "Visuel de couverture vidéo (One More Gaze)", ratio: "16 / 9", pos: "center", crop: "Paysage 16:9 · s'affiche avant lecture" },
  { slot: "manifesto", label: "Photo « La vision »", ratio: "3 / 4", pos: "center", crop: "Portrait vertical" },
  { slot: "newsletter", label: "Fond de la Newsletter", ratio: "16 / 9", pos: "center", crop: "Panoramique · en fond" },
];

const fmtDate = (d) => {
  const parts = d.split("-");
  return `${parts[2]}/${parts[1]}`;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-elevated border border-gold/30 px-3 py-2 text-xs">
      <p className="text-[#A3A3A3] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={p.dataKey || p.name || i} style={{ color: p.color || GOLD }} className="capitalize">
          {p.name}: <span className="text-white font-medium">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, i }) => (
  <motion.div
    initial={CARD_INITIAL}
    animate={CARD_ANIMATE}
    transition={{ delay: i * 0.06, duration: 0.5 }}
    data-testid={`stat-card-${i}`}
    className="backdrop-blur-2xl bg-white/[0.02] border border-white/[0.08] p-5 sm:p-6"
  >
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">{label}</span>
      <Icon className="w-4 h-4 text-gold" />
    </div>
    <p className="font-serif text-4xl sm:text-5xl font-light gold-text leading-none">{value}</p>
  </motion.div>
);

const Panel = ({ title, sub, children, testid }) => (
  <div data-testid={testid} className="backdrop-blur-2xl bg-white/[0.02] border border-white/[0.08] p-5 sm:p-7">
    <div className="mb-6">
      <h3 className="font-serif text-2xl text-white">{title}</h3>
      {sub && <p className="text-xs text-[#8A8A8A] mt-1">{sub}</p>}
    </div>
    {children}
  </div>
);

const TRACKERS = [
  { id: "instagram", name: "Instagram", Icon: InstagramIcon },
  { id: "tiktok", name: "TikTok", Icon: TikTokIcon },
  { id: "youtube", name: "YouTube", Icon: YouTubeIcon },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [settings, setSettings] = useState({ welcome_message: "", archive_days: 7, featured_video_id: "" });
  const [gallery, setGallery] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);
  const [form, setForm] = useState({ spotify: "", instagram: "", tiktok: "", youtube: "" });
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({ hero_tagline: "", bio: "", about_title: "", about_paragraphs: "" });
  const [presave, setPresave] = useState({ enabled: false, title: "", subtitle: "", spotify_url: "", apple_url: "", note: "" });
  const [pubStats, setPubStats] = useState({ spotify_listeners: "", myturn_streams: "", instagram: "", tiktok: "", youtube: "" });
  const [siteImages, setSiteImages] = useState({});
  const [pendingSlot, setPendingSlot] = useState("");
  const [imgBusy, setImgBusy] = useState("");
  const siteFileRef = React.useRef(null);
  const [salonAudio, setSalonAudio] = useState({});
  const [audioBusy, setAudioBusy] = useState(false);
  const audioFileRef = React.useRef(null);
  const [artistMsg, setArtistMsg] = useState("");
  const [posting, setPosting] = useState(false);
  const [reported, setReported] = useState([]);
  const [bans, setBans] = useState([]);
  const [banForm, setBanForm] = useState({ type: "username", value: "", reason: "" });
  const [salonMsgs, setSalonMsgs] = useState([]);
  const [replyTarget, setReplyTarget] = useState(null);
  const [topMsgs, setTopMsgs] = useState([]);
  const fileRef = React.useRef(null);

  const loadGallery = useCallback(async () => {
    try {
      const { data } = await api.get(`/gallery`);
      setGallery(data);
    } catch (e) {
      logError("Gallery load failed", e);
    }
  }, []);

  const loadModeration = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/chat/reported`);
      setReported(data);
    } catch (e) {
      logError("Reported load failed", e);
    }
    try {
      const { data } = await api.get(`/admin/chat/bans`);
      setBans(data);
    } catch (e) {
      logError("Bans load failed", e);
    }
    try {
      const { data } = await api.get(`/admin/chat/messages`);
      setSalonMsgs(data);
    } catch (e) {
      logError("Salon messages load failed", e);
    }
    try {
      const { data } = await api.get(`/admin/chat/top`);
      setTopMsgs(data);
    } catch (e) {
      logError("Top reactions load failed", e);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/stats?days=${days}`);
      setStats(data);
    } catch (e) {
      toast.error("Impossible de charger les statistiques");
    }
    try {
      const { data } = await api.get(`/admin/subscribers`);
      setSubs(data);
    } catch (e) {
      logError("Subscribers load failed", e);
    }
    try {
      const { data } = await api.get(`/admin/settings`);
      setSettings(data);
      setContent({
        hero_tagline: data.hero_tagline || "",
        bio: data.bio || "",
        about_title: data.about_title || "",
        about_paragraphs: (data.about_paragraphs || []).join("\n\n"),
      });
      setPresave({
        enabled: !!data.presave?.enabled,
        title: data.presave?.title || "",
        subtitle: data.presave?.subtitle || "",
        spotify_url: data.presave?.spotify_url || "",
        apple_url: data.presave?.apple_url || "",
        note: data.presave?.note || "",
      });
      setPubStats({
        spotify_listeners: data.stats?.spotify_listeners || "",
        myturn_streams: data.stats?.myturn_streams || "",
        instagram: data.stats?.instagram || "",
        tiktok: data.stats?.tiktok || "",
        youtube: data.stats?.youtube || "",
      });
      setSiteImages(data.images || {});
      setSalonAudio(data.salon_audio || {});
    } catch (e) {
      logError("Settings load failed", e);
    }
    try {
      const { data } = await api.get(`/admin/contacts`);
      setContacts(data);
    } catch (e) {
      logError("Contacts load failed", e);
    }
    loadGallery();
  }, [days, loadGallery]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadModeration(); }, [loadModeration]);

  const exportCSV = () => {
    const rows = [["email", "date"], ...subs.map((s) => [s.email || "", (s.created_at || "").slice(0, 10)])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inscrits-akalibe.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/settings", {
        welcome_message: settings.welcome_message,
        archive_days: Number(settings.archive_days) || 7,
        featured_video_id: settings.featured_video_id,
      });
      setSettings(data);
      toast.success("Réglages enregistrés ✦");
    } catch (err) {
      toast.error("Enregistrement impossible");
    }
  };

  const saveContent = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", {
        hero_tagline: content.hero_tagline,
        bio: content.bio,
        about_title: content.about_title,
        about_paragraphs: content.about_paragraphs.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Textes du site enregistrés ✦");
    } catch (err) {
      toast.error("Enregistrement impossible");
    }
  };

  const savePresave = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", { presave });
      toast.success(presave.enabled ? "Section pré-enregistrement activée ✦" : "Section pré-enregistrement enregistrée");
    } catch (err) {
      toast.error("Enregistrement impossible");
    }
  };

  const savePublicStats = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", {
        stats: {
          spotify_listeners: Number(pubStats.spotify_listeners) || 0,
          myturn_streams: Number(pubStats.myturn_streams) || 0,
          instagram: Number(pubStats.instagram) || 0,
          tiktok: Number(pubStats.tiktok) || 0,
          youtube: Number(pubStats.youtube) || 0,
        },
      });
      toast.success("Chiffres publics mis à jour ✦");
    } catch (err) {
      toast.error("Enregistrement impossible");
    }
  };

  const pickSiteImage = (slot) => {
    setPendingSlot(slot);
    siteFileRef.current?.click();
  };

  const uploadSiteImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingSlot) return;
    setImgBusy(pendingSlot);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/site-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...siteImages, [pendingSlot]: data.url };
      setSiteImages(next);
      await api.put("/admin/settings", { images: next });
      toast.success("Image mise à jour ✦ (visible après rechargement du site)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload impossible");
    } finally {
      setImgBusy("");
      setPendingSlot("");
      if (siteFileRef.current) siteFileRef.current.value = "";
    }
  };

  const siteImgPreview = (slot) => {
    const v = siteImages[slot];
    if (!v) return null;
    return v.startsWith("http") ? v : `${BACKEND}${v}`;
  };

  const uploadSalonAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAudioBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/salon-audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSalonAudio(data);
      toast.success("Extrait audio publié dans le Salon ✦");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload impossible");
    } finally {
      setAudioBusy(false);
      if (audioFileRef.current) audioFileRef.current.value = "";
    }
  };

  const removeSalonAudio = async () => {
    try {
      await api.put("/admin/settings", { salon_audio: {} });
      setSalonAudio({});
      toast.success("Extrait retiré du Salon");
    } catch (err) {
      toast.error("Action impossible");
    }
  };

  const postAsArtist = async (e) => {
    e.preventDefault();
    const msg = artistMsg.trim();
    if (!msg) return;
    setPosting(true);
    try {
      await api.post("/admin/chat/messages", { message: msg, reply_to_id: replyTarget?.id || null });
      setArtistMsg("");
      setReplyTarget(null);
      toast.success(replyTarget ? "Réponse publiée dans le Salon ✦" : "Message publié dans le Salon en tant qu'AKALIBE ✦");
      loadModeration();
    } catch (err) {
      toast.error("Envoi impossible");
    } finally {
      setPosting(false);
    }
  };

  const startReply = (m) => {
    setReplyTarget(m);
    document.querySelector('[data-testid="artist-message-input"]')?.focus();
    document.querySelector('[data-testid="panel-artist-post"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const pinMsg = async (id) => {
    try {
      await api.post(`/admin/chat/messages/${id}/pin`);
      toast.success("Message épinglé en haut du Salon 📌");
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const unpinMsg = async (id) => {
    try {
      await api.post(`/admin/chat/messages/${id}/unpin`);
      toast.success("Épingle retirée");
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const hideMsg = async (id) => {
    try {
      await api.post(`/admin/chat/messages/${id}/hide`);
      toast.success("Message masqué du Salon");
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const unhideMsg = async (id) => {
    try {
      await api.post(`/admin/chat/messages/${id}/unhide`);
      toast.success("Message ré-affiché");
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const dismissReports = async (id) => {
    try {
      await api.post(`/admin/chat/messages/${id}/dismiss`);
      toast.success("Signalements ignorés");
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const deleteMsg = async (id) => {
    if (!window.confirm("Supprimer définitivement ce message ?")) return;
    try {
      await api.delete(`/admin/chat/messages/${id}`);
      toast.success("Message supprimé");
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const addBan = async (e) => {
    e.preventDefault();
    const value = banForm.value.trim();
    if (!value) return;
    try {
      await api.post("/admin/chat/ban", { type: banForm.type, value, reason: banForm.reason.trim() });
      setBanForm({ type: banForm.type, value: "", reason: "" });
      toast.success("Bannissement ajouté");
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const banFromMessage = async (username) => {
    try {
      await api.post("/admin/chat/ban", { type: "username", value: username, reason: "Signalé dans le Salon" });
      toast.success(`« ${username} » banni du Salon`);
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const removeBan = async (id) => {
    try {
      await api.delete(`/admin/chat/ban/${id}`);
      toast.success("Bannissement levé");
      loadModeration();
    } catch { toast.error("Action impossible"); }
  };

  const resetSiteImage = async (slot) => {
    setImgBusy(slot);
    const next = { ...siteImages };
    delete next[slot];
    try {
      await api.put("/admin/settings", { images: { ...next, [slot]: "" } });
      setSiteImages(next);
      toast.success("Image réinitialisée (valeur par défaut) ✦");
    } catch (err) {
      toast.error("Action impossible");
    } finally {
      setImgBusy("");
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    try {
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image ajoutée à la galerie ✦");
      setCaption("");
      loadGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload impossible");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteImage = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      setGallery((g) => g.filter((x) => x.id !== id));
      toast.success("Image supprimée");
    } catch (err) {
      toast.error("Suppression impossible");
    }
  };

  const saveSnapshot = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/snapshots", {
        spotify: Number(form.spotify) || 0,
        instagram: Number(form.instagram) || 0,
        tiktok: Number(form.tiktok) || 0,
        youtube: Number(form.youtube) || 0,
      });
      toast.success("Chiffres enregistrés ✦");
      setForm({ spotify: "", instagram: "", tiktok: "", youtube: "" });
      load();
    } catch (e) {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const clicks = stats?.clicks_by_platform || {};
  const topPlatform = TRACKERS.reduce(
    (top, t) => ((clicks[t.id] || 0) > (clicks[top?.id] || -1) ? t : top),
    null
  );
  const clickBars = TRACKERS.map((t) => ({ name: t.name, clics: clicks[t.id] || 0 }));

  return (
    <div className="min-h-screen bg-base grain text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-black/50 border-b border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="AKALIBE" className="h-9 w-auto" />
            <div className="hidden sm:block border-l border-white/10 pl-4">
              <p className="text-sm text-white leading-tight">Tableau de bord</p>
              <p className="text-[10px] text-[#8A8A8A] uppercase tracking-widest">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-white/[0.1] text-xs">
              {[7, 30].map((d) => (
                <button
                  key={d}
                  data-testid={`period-${d}`}
                  onClick={() => setDays(d)}
                  className={`px-3 py-2 transition-colors ${days === d ? "bg-gold text-black" : "text-[#A3A3A3] hover:text-white"}`}
                >
                  {d}j
                </button>
              ))}
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 border border-white/[0.1] px-4 py-2 text-sm text-[#A3A3A3] hover:text-gold hover:border-gold/40 transition-colors"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8 sm:py-12 space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Bienvenue dans ton univers</p>
          <h1 className="font-serif font-light text-3xl sm:text-5xl tracking-tight mt-2">
            Tes statistiques, en direct.
          </h1>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <StatCard i={0} icon={Eye} label="Vues aujourd'hui" value={stats?.summary?.today_views ?? "…"} />
          <StatCard i={1} icon={Users} label="Visiteurs uniques (jour)" value={stats?.summary?.today_unique ?? "…"} />
          <StatCard i={2} icon={Mail} label="Abonnés newsletter" value={stats?.summary?.total_subscribers ?? "…"} />
          <StatCard i={3} icon={MousePointerClick} label="Clics liens (total)" value={stats?.summary?.total_clicks ?? "…"} />
        </div>

        {/* Traffic + Subscribers charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Panel testid="panel-traffic" title="Trafic du site" sub={`Vues & visiteurs uniques · ${days} derniers jours`}>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.visitors_series || []} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={TICK_SM} axisLine={false} tickLine={false} />
                  <YAxis tick={TICK_SM} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="views" name="vues" stroke={GOLD} strokeWidth={2} fill="url(#gViews)" />
                  <Line type="monotone" dataKey="unique" name="uniques" stroke="#F9F1D8" strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testid="panel-subscribers" title="Évolution des abonnés" sub={`Inscrits newsletter (cumulé) · ${days} derniers jours`}>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.subscribers_series || []} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="gSubs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={TICK_SM} axisLine={false} tickLine={false} />
                  <YAxis tick={TICK_SM} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="total" name="abonnés" stroke={GOLD} strokeWidth={2} fill="url(#gSubs)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* Newsletter subscribers list */}
        <Panel testid="panel-subscribers-list" title="Mes inscrits newsletter" sub={`${subs.length} personne(s) inscrite(s) · les plus récents en premier`}>
          <button
            data-testid="export-csv-btn"
            onClick={exportCSV}
            disabled={subs.length === 0}
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 text-xs uppercase tracking-widest hover:bg-gold/10 transition-colors disabled:opacity-40 mb-5"
          >
            <Download className="w-3.5 h-3.5" /> Exporter en CSV
          </button>
          {subs.length === 0 ? (
            <p className="text-sm text-[#8A8A8A]">Aucun inscrit pour le moment.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-white/[0.06]">
              {subs.map((s, i) => (
                <div key={s.email || `sub-${i}`} data-testid={`subscriber-row-${i}`} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-8 h-8 shrink-0 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold text-xs">
                      {(s.email || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="text-sm text-white truncate">{s.email}</span>
                  </div>
                  <span className="text-xs text-[#8A8A8A] shrink-0 ml-4">{(s.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Trackers */}
        <Panel testid="panel-trackers" title="Trackers de liens" sub="Quel réseau génère le plus de trafic ? (clics sortants depuis le site)">
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {TRACKERS.map((t) => {
              const count = clicks[t.id] || 0;
              const isTop = topPlatform?.id === t.id && count > 0;
              return (
                <div
                  key={t.id}
                  data-testid={`tracker-${t.id}`}
                  className={`relative border p-6 transition-colors ${isTop ? "border-gold bg-gold/[0.06]" : "border-white/[0.08] bg-white/[0.02]"}`}
                >
                  {isTop && (
                    <span className="absolute top-3 right-3 flex items-center gap-1 text-[9px] uppercase tracking-widest bg-gold text-black px-2 py-0.5">
                      <TrendingUp className="w-3 h-3" /> Top
                    </span>
                  )}
                  <t.Icon size={26} className={isTop ? "text-gold" : "text-white/70"} />
                  <p className="text-white font-medium mt-4">{t.name}</p>
                  <p className="font-serif text-4xl font-light gold-text mt-1">{count}</p>
                  <p className="text-xs text-[#8A8A8A]">clics</p>
                </div>
              );
            })}
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={clickBars} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="name" tick={TICK_MD} axisLine={false} tickLine={false} />
                <YAxis tick={TICK_SM} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="clics" fill={GOLD} radius={BAR_RADIUS} maxBarSize={90} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        {/* Manual snapshot */}
        <Panel testid="panel-snapshot" title="Enregistrer mes chiffres" sub="Note tes abonnés du jour pour suivre leur évolution (Spotify, Insta, TikTok, YouTube)">
          <form onSubmit={saveSnapshot} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {["spotify", "instagram", "tiktok", "youtube"].map((k) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] block mb-2 capitalize">{k}</label>
                <input
                  data-testid={`snapshot-${k}`}
                  type="number"
                  min="0"
                  value={form[k]}
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  placeholder="0"
                  className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
            ))}
            <button
              data-testid="snapshot-save"
              disabled={saving}
              className="col-span-2 sm:col-span-4 sm:w-auto sm:justify-self-start inline-flex items-center justify-center gap-2 bg-gold text-black font-medium px-8 py-3.5 hover:bg-gold-light transition-colors disabled:opacity-50 text-sm tracking-wide"
            >
              <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>

          {stats?.snapshots?.length > 0 && (
            <div className="h-56 mt-8">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.snapshots} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={TICK_SM} axisLine={false} tickLine={false} />
                  <YAxis tick={TICK_SM} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line type="monotone" dataKey="spotify" stroke="#1DB954" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="instagram" stroke="#E1306C" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="tiktok" stroke="#F9F1D8" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="youtube" stroke="#FF0000" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        {/* Gallery manager */}
        <Panel testid="panel-gallery" title="Ma galerie" sub="Ajoute et gère tes photos, elles apparaissent directement sur ton site">
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <input
              data-testid="gallery-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Légende (optionnelle) ex : Live à Lyon"
              className="flex-1 bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
            />
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="gallery-file-input" />
            <button
              data-testid="gallery-upload-btn"
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 bg-gold text-black font-medium px-6 py-3 hover:bg-gold-light transition-colors disabled:opacity-50 text-sm tracking-wide"
            >
              <ImagePlus className="w-4 h-4" /> {busy ? "Envoi…" : "Ajouter une photo"}
            </button>
          </div>

          {gallery.length === 0 ? (
            <p className="text-sm text-[#8A8A8A]">Aucune photo pour l'instant. Ajoute ta première image ✦</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((g) => (
                <div key={g.id} data-testid={`gallery-manage-${g.id}`} className="group relative aspect-square overflow-hidden border border-white/[0.08]">
                  <img src={`${BACKEND}${g.url}`} alt={g.caption || ""} className="w-full h-full object-cover" />
                  <button
                    data-testid={`gallery-delete-${g.id}`}
                    onClick={() => deleteImage(g.id)}
                    className="absolute top-2 right-2 w-9 h-9 flex items-center justify-center bg-black/70 border border-white/20 text-white hover:bg-red-500 hover:border-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {g.caption && (
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent text-xs text-white/90 truncate">{g.caption}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Site texts editor */}
        <Panel testid="panel-content" title="Textes du site" sub="Modifie ta bio et ta section « À propos », mise à jour instantanée sur le site">
          <form onSubmit={saveContent} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-2 mb-2">
                <Type className="w-3.5 h-3.5 text-gold" /> Bio courte (accueil)
              </label>
              <textarea
                data-testid="content-bio"
                rows={4}
                value={content.bio}
                onChange={(e) => setContent((c) => ({ ...c, bio: e.target.value }))}
                placeholder="Je suis Akalibe…"
                className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors resize-none"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-2 mb-2">
                <Type className="w-3.5 h-3.5 text-gold" /> Titre « À propos »
              </label>
              <input
                data-testid="content-about-title"
                value={content.about_title}
                onChange={(e) => setContent((c) => ({ ...c, about_title: e.target.value }))}
                placeholder="Akalibe : le voyage d'une étoile"
                className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
              />
              <p className="text-[11px] text-[#8A8A8A] mt-2">La partie après « : » s'affiche en doré et en italique.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-2 mb-2">
                <Type className="w-3.5 h-3.5 text-gold" /> Paragraphes « À propos »
              </label>
              <textarea
                data-testid="content-about-paragraphs"
                rows={8}
                value={content.about_paragraphs}
                onChange={(e) => setContent((c) => ({ ...c, about_paragraphs: e.target.value }))}
                placeholder={"Premier paragraphe…\n\nDeuxième paragraphe…"}
                className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors resize-none"
              />
              <p className="text-[11px] text-[#8A8A8A] mt-2">Sépare chaque paragraphe par une <span className="text-gold">ligne vide</span>.</p>
            </div>
            <button
              data-testid="content-save"
              className="inline-flex items-center gap-2 bg-gold text-black font-medium px-8 py-3.5 hover:bg-gold-light transition-colors text-sm tracking-wide"
            >
              <Save className="w-4 h-4" /> Enregistrer les textes
            </button>
          </form>
        </Panel>

        {/* Public stats editor */}
        <Panel testid="panel-public-stats" title="Chiffres affichés sur le site" sub="Renseigne tes vrais chiffres, ils apparaissent sur le hub de suivi et se mettent à jour instantanément">
          <form onSubmit={savePublicStats} className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { k: "spotify_listeners", label: "Auditeurs Spotify" },
              { k: "myturn_streams", label: "Écoutes « My Turn »" },
              { k: "instagram", label: "Abonnés Instagram" },
              { k: "tiktok", label: "Abonnés TikTok" },
              { k: "youtube", label: "Abonnés YouTube" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] block mb-2">{f.label}</label>
                <input
                  data-testid={`pubstat-${f.k}`}
                  type="number"
                  min="0"
                  value={pubStats[f.k]}
                  onChange={(e) => setPubStats((s) => ({ ...s, [f.k]: e.target.value }))}
                  placeholder="0"
                  className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
            ))}
            <button
              data-testid="pubstat-save"
              className="col-span-2 sm:col-span-3 sm:w-auto sm:justify-self-start inline-flex items-center justify-center gap-2 bg-gold text-black font-medium px-8 py-3.5 hover:bg-gold-light transition-colors text-sm tracking-wide"
            >
              <BarChart3 className="w-4 h-4" /> Publier mes chiffres
            </button>
          </form>
          <p className="text-[11px] text-[#8A8A8A] mt-4">Laisse à 0 pour masquer un compteur. Aucun chiffre n'est inventé : ce que tu saisis ici est exactement ce qui s'affiche.</p>
        </Panel>

        {/* Site images manager */}
        <Panel testid="panel-site-images" title="Images du site" sub="Remplace n'importe quelle image du site, upload depuis ton ordinateur (JPG, PNG, WEBP, max 12 Mo)">
          <input ref={siteFileRef} type="file" accept="image/*" onChange={uploadSiteImage} className="hidden" data-testid="site-image-input" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SITE_IMAGE_SLOTS.map(({ slot, label, ratio, pos, crop }) => (
              <div key={slot} data-testid={`site-image-${slot}`} className="border border-white/[0.08] bg-black/30">
                <div className="relative bg-black/50 overflow-hidden flex items-center justify-center" style={{ aspectRatio: ratio }}>
                  {siteImgPreview(slot) ? (
                    <img src={siteImgPreview(slot)} alt={label} className="w-full h-full object-cover" style={{ objectPosition: pos }} />
                  ) : (
                    <span className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">Image par défaut</span>
                  )}
                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.15em] text-white/70 bg-black/50 backdrop-blur-sm px-2 py-0.5">
                    {crop}
                  </span>
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-white truncate">{label}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {siteImgPreview(slot) && (
                        <button
                          data-testid={`site-image-reset-${slot}`}
                          onClick={() => resetSiteImage(slot)}
                          disabled={imgBusy === slot}
                          className="text-[11px] text-[#8A8A8A] hover:text-white px-2 py-1.5 transition-colors disabled:opacity-50"
                        >
                          Défaut
                        </button>
                      )}
                      <button
                        data-testid={`site-image-replace-${slot}`}
                        onClick={() => pickSiteImage(slot)}
                        disabled={imgBusy === slot}
                        className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-1.5 text-xs hover:bg-gold hover:text-black transition-colors disabled:opacity-50"
                      >
                        <ImagePlus className="w-3.5 h-3.5" /> {imgBusy === slot ? "…" : "Remplacer"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        {/* Salon — exclusive audio extract */}
        <Panel testid="panel-salon-audio" title="Salon — extrait audio exclusif" sub="Mets en ligne un extrait (MP3, M4A, WAV… max 25 Mo). Il apparaît dans le Salon pour les fans. Tu peux le changer quand tu veux.">
          <input ref={audioFileRef} type="file" accept="audio/*" onChange={uploadSalonAudio} className="hidden" data-testid="salon-audio-input" />
          {salonAudio?.url ? (
            <div className="space-y-4">
              <audio data-testid="salon-audio-admin-el" controls preload="none" src={`${BACKEND}${salonAudio.url}`} className="w-full" />
              <p className="text-xs text-[#8A8A8A] truncate">🎧 {salonAudio.name || "Extrait en ligne"}</p>
              <div className="flex gap-3">
                <button data-testid="salon-audio-replace" onClick={() => audioFileRef.current?.click()} disabled={audioBusy}
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 text-sm hover:bg-gold hover:text-black transition-colors disabled:opacity-50">
                  <Music className="w-4 h-4" /> {audioBusy ? "Envoi…" : "Remplacer l'extrait"}
                </button>
                <button data-testid="salon-audio-remove" onClick={removeSalonAudio}
                  className="inline-flex items-center gap-2 border border-white/[0.12] text-[#A3A3A3] px-5 py-2.5 text-sm hover:text-white transition-colors">
                  <Trash2 className="w-4 h-4" /> Retirer
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-[#8A8A8A] mb-5">Aucun extrait en ligne — le lecteur reste masqué côté fans.</p>
              <button data-testid="salon-audio-upload" onClick={() => audioFileRef.current?.click()} disabled={audioBusy}
                className="inline-flex items-center gap-2 bg-gold text-black font-medium px-8 py-3.5 text-sm hover:bg-gold-light transition-colors disabled:opacity-50">
                <Music className="w-4 h-4" /> {audioBusy ? "Envoi…" : "Mettre un extrait en ligne"}
              </button>
            </div>
          )}
        </Panel>

        {/* Salon — post as AKALIBE (moved from public for security) */}
        <Panel testid="panel-top-reactions" title="Salon — top réactions" sub="Les messages de fans les plus aimés (❤️ 🔥 🎵 😍 👏). Repère d'un coup d'œil ce qui touche ta communauté.">
          {topMsgs.length === 0 ? (
            <p data-testid="top-reactions-empty" className="text-sm text-[#8A8A8A]">Aucune réaction pour le moment ✦ Dès que les fans réagissent, le classement apparaît ici.</p>
          ) : (
            <div className="space-y-3" data-testid="top-reactions-list">
              {topMsgs.map((m, idx) => (
                <div key={m.id} data-testid={`top-msg-${m.id}`}
                  className="flex items-start gap-3 border border-white/[0.08] bg-white/[0.02] p-4">
                  <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                    style={idx === 0
                      ? { background: "linear-gradient(135deg,#D4AF37,#B8860B)", color: "#000" }
                      : { background: "rgba(255,255,255,0.05)", color: "#A3A3A3", border: "1px solid rgba(255,255,255,0.1)" }}>
                    {idx + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-medium text-white/90">{m.username}</span>
                      <span data-testid={`top-count-${m.id}`} className="inline-flex items-center gap-1 text-[11px] text-gold bg-gold/10 border border-gold/20 px-2 py-0.5 rounded-full">
                        <Heart className="w-3 h-3" fill="currentColor" /> {m.total_reactions}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs">
                        {Object.entries(m.reactions || {}).map(([emoji, count]) => (
                          <span key={emoji} className="text-[#A3A3A3]">{emoji} {count}</span>
                        ))}
                      </span>
                    </div>
                    <p className="text-sm text-white/70 break-words">{m.message}</p>
                  </div>
                  <div className="shrink-0 flex flex-col gap-2">
                    {m.is_pinned ? (
                      <button data-testid={`top-unpin-${m.id}`} onClick={() => unpinMsg(m.id)}
                        className="inline-flex items-center gap-1 text-xs border border-gold/40 px-3 py-2 text-gold hover:bg-gold/10 transition-colors">
                        <Pin className="w-3.5 h-3.5" /> Épinglé
                      </button>
                    ) : (
                      <button data-testid={`top-pin-${m.id}`} onClick={() => pinMsg(m.id)}
                        className="inline-flex items-center gap-1 text-xs border border-white/[0.15] px-3 py-2 text-white/70 hover:border-gold/50 hover:text-gold transition-colors">
                        <Pin className="w-3.5 h-3.5" /> Épingler
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="panel-salon-live" title="Salon — fil en direct" sub="Vois les derniers messages des fans. Réponds à un message précis (avec citation) ou épingle-en un tout en haut du Salon pour honorer ta communauté.">
          {salonMsgs.length === 0 ? (
            <p data-testid="salon-live-empty" className="text-sm text-[#8A8A8A]">Aucun message pour le moment ✦</p>
          ) : (
            <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="salon-live-list">
              {salonMsgs.map((m) => (
                <div key={m.id} data-testid={`salon-msg-${m.id}`}
                  className={`border p-4 flex flex-col sm:flex-row sm:items-start gap-3 ${m.is_pinned ? "border-gold/40 bg-gold/[0.05]" : "border-white/[0.08] bg-white/[0.02]"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-sm font-medium ${m.is_artist ? "text-gold" : "text-white/90"}`}>{m.username}</span>
                      {m.is_artist && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-gold text-black px-1.5 py-0.5"><Crown className="w-3 h-3" /> Artiste</span>}
                      {m.is_pinned && <span className="inline-flex items-center gap-1 text-[10px] text-gold uppercase tracking-widest"><Pin className="w-3 h-3" /> Épinglé</span>}
                      {m.is_hidden && <span className="text-[10px] text-[#8A8A8A] uppercase tracking-widest">masqué</span>}
                    </div>
                    {m.reply_to && (
                      <div className="border-l-2 border-gold/40 pl-2 mb-1 text-xs text-[#9A9A9A] italic">↳ {m.reply_to.username} : {m.reply_to.message}</div>
                    )}
                    <p className="text-sm text-white/70 break-words">{m.message}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {!m.is_artist && (
                      <button data-testid={`reply-btn-${m.id}`} onClick={() => startReply(m)}
                        className="inline-flex items-center gap-1 text-xs border border-white/[0.15] px-3 py-2 text-white/70 hover:border-gold/50 hover:text-gold transition-colors">
                        <Reply className="w-3.5 h-3.5" /> Répondre
                      </button>
                    )}
                    {m.is_pinned ? (
                      <button data-testid={`unpin-btn-${m.id}`} onClick={() => unpinMsg(m.id)}
                        className="inline-flex items-center gap-1 text-xs border border-gold/40 px-3 py-2 text-gold hover:bg-gold/10 transition-colors">
                        <Pin className="w-3.5 h-3.5" /> Retirer l'épingle
                      </button>
                    ) : (
                      <button data-testid={`pin-btn-${m.id}`} onClick={() => pinMsg(m.id)}
                        className="inline-flex items-center gap-1 text-xs border border-white/[0.15] px-3 py-2 text-white/70 hover:border-gold/50 hover:text-gold transition-colors">
                        <Pin className="w-3.5 h-3.5" /> Épingler
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="panel-artist-post" title="Salon — publier en tant qu'AKALIBE" sub="Écris un message qui s'affichera dans le Salon avec ton badge doré « ARTISTE ». Réservé à toi (connexion admin).">
          <form onSubmit={postAsArtist} className="space-y-4">
            {replyTarget && (
              <div data-testid="reply-preview" className="flex items-start justify-between gap-3 border-l-2 border-gold/50 bg-gold/[0.05] px-3 py-2">
                <div className="min-w-0">
                  <span className="text-[11px] text-gold uppercase tracking-widest">En réponse à {replyTarget.username}</span>
                  <p className="text-xs text-[#9A9A9A] italic truncate">{replyTarget.message}</p>
                </div>
                <button type="button" data-testid="reply-cancel-btn" onClick={() => setReplyTarget(null)}
                  className="shrink-0 text-[#8A8A8A] hover:text-white transition-colors"><X className="w-4 h-4" /></button>
              </div>
            )}
            <div className="flex items-start gap-3">
              <span className="mt-1 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest bg-gold text-black px-2 py-1 shrink-0">
                <Crown className="w-3 h-3" /> Artiste
              </span>
              <textarea
                data-testid="artist-message-input"
                rows={3}
                maxLength={500}
                value={artistMsg}
                onChange={(e) => setArtistMsg(e.target.value)}
                placeholder="Ton message pour les fans du Salon…"
                className="flex-1 min-w-0 bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors resize-none"
              />
            </div>
            <button data-testid="artist-post-btn" disabled={posting}
              className="inline-flex items-center gap-2 bg-gold text-black font-medium px-8 py-3.5 text-sm hover:bg-gold-light transition-colors disabled:opacity-50">
              <MessageCircle className="w-4 h-4" /> {posting ? "Publication…" : "Publier dans le Salon"}
            </button>
          </form>
        </Panel>

        <Panel testid="panel-moderation" title="Salon — modération" sub="Messages signalés par la communauté. Masque, supprime ou ignore les signalements. Tu peux aussi bannir l'auteur.">
          {reported.length === 0 ? (
            <p data-testid="moderation-empty" className="text-sm text-[#8A8A8A]">Aucun message signalé pour le moment ✦</p>
          ) : (
            <div className="space-y-3" data-testid="moderation-list">
              {reported.map((m) => (
                <div key={m.id} data-testid={`reported-msg-${m.id}`}
                  className="border border-white/[0.08] bg-white/[0.02] p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-medium text-white/90">{m.username}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 px-1.5 py-0.5 rounded-full">
                        <Flag className="w-3 h-3" /> {m.report_count} signalement{m.report_count > 1 ? "s" : ""}
                      </span>
                      {m.is_hidden && <span className="text-[11px] text-[#8A8A8A] uppercase tracking-widest">masqué</span>}
                    </div>
                    <p className="text-sm text-white/70 break-words">{m.message}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {m.is_hidden ? (
                      <button data-testid={`unhide-btn-${m.id}`} onClick={() => unhideMsg(m.id)}
                        className="inline-flex items-center gap-1 text-xs border border-white/[0.15] px-3 py-2 text-white/70 hover:border-gold/50 hover:text-gold transition-colors">
                        <Eye className="w-3.5 h-3.5" /> Ré-afficher
                      </button>
                    ) : (
                      <button data-testid={`hide-btn-${m.id}`} onClick={() => hideMsg(m.id)}
                        className="inline-flex items-center gap-1 text-xs border border-white/[0.15] px-3 py-2 text-white/70 hover:border-gold/50 hover:text-gold transition-colors">
                        <EyeOff className="w-3.5 h-3.5" /> Masquer
                      </button>
                    )}
                    <button data-testid={`ban-user-btn-${m.id}`} onClick={() => banFromMessage(m.username)}
                      className="inline-flex items-center gap-1 text-xs border border-white/[0.15] px-3 py-2 text-white/70 hover:border-red-500/50 hover:text-red-400 transition-colors">
                      <Ban className="w-3.5 h-3.5" /> Bannir
                    </button>
                    <button data-testid={`dismiss-btn-${m.id}`} onClick={() => dismissReports(m.id)}
                      className="inline-flex items-center gap-1 text-xs border border-white/[0.15] px-3 py-2 text-white/60 hover:text-white transition-colors">
                      Ignorer
                    </button>
                    <button data-testid={`delete-msg-btn-${m.id}`} onClick={() => deleteMsg(m.id)}
                      className="inline-flex items-center gap-1 text-xs border border-red-500/30 px-3 py-2 text-red-400 hover:bg-red-500/10 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="panel-bans" title="Salon — bannissements" sub="Bloque un pseudo ou une adresse IP pour l'empêcher d'écrire dans le Salon. Anti-spam : 1 message toutes les 3 secondes par personne.">
          <form onSubmit={addBan} className="flex flex-col sm:flex-row gap-3 mb-6">
            <select data-testid="ban-type-select" value={banForm.type}
              onChange={(e) => setBanForm({ ...banForm, type: e.target.value })}
              className="bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white focus:outline-none focus:border-gold/50">
              <option value="username">Pseudo</option>
              <option value="ip">Adresse IP</option>
            </select>
            <input data-testid="ban-value-input" value={banForm.value}
              onChange={(e) => setBanForm({ ...banForm, value: e.target.value })}
              placeholder={banForm.type === "ip" ? "Ex : 82.14.x.x" : "Pseudo à bannir"}
              className="flex-1 min-w-0 bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50" />
            <input data-testid="ban-reason-input" value={banForm.reason}
              onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
              placeholder="Raison (optionnel)"
              className="flex-1 min-w-0 bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50" />
            <button data-testid="ban-add-btn"
              className="inline-flex items-center justify-center gap-2 bg-gold text-black font-medium px-6 py-3 text-sm hover:bg-gold-light transition-colors">
              <Ban className="w-4 h-4" /> Bannir
            </button>
          </form>
          {bans.length === 0 ? (
            <p data-testid="bans-empty" className="text-sm text-[#8A8A8A]">Aucun bannissement actif ✦</p>
          ) : (
            <div className="space-y-2" data-testid="bans-list">
              {bans.map((b) => (
                <div key={b.id} data-testid={`ban-row-${b.id}`}
                  className="flex items-center justify-between gap-3 border border-white/[0.08] bg-white/[0.02] px-4 py-3">
                  <div className="min-w-0">
                    <span className="text-sm text-white/90">
                      <span className="text-[11px] uppercase tracking-widest text-gold mr-2">{b.type === "ip" ? "IP" : "Pseudo"}</span>
                      {b.value}
                    </span>
                    {b.reason && <p className="text-xs text-[#8A8A8A] truncate">{b.reason}</p>}
                  </div>
                  <button data-testid={`unban-btn-${b.id}`} onClick={() => removeBan(b.id)}
                    className="shrink-0 inline-flex items-center gap-1 text-xs border border-white/[0.15] px-3 py-2 text-white/70 hover:border-gold/50 hover:text-gold transition-colors">
                    Lever
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Pre-save section editor */}
        <Panel testid="panel-presave" title="Section « Pré-enregistrement »" sub="Prépare le lancement de ton prochain single, la section reste masquée tant qu'elle n'est pas activée">
          <form onSubmit={savePresave} className="space-y-6">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                data-testid="presave-enabled"
                type="checkbox"
                checked={presave.enabled}
                onChange={(e) => setPresave((p) => ({ ...p, enabled: e.target.checked }))}
                className="w-5 h-5 accent-[#D4AF37]"
              />
              <span className="text-sm text-white flex items-center gap-2">
                <Rocket className="w-4 h-4 text-gold" /> Afficher la section sur le site
              </span>
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] block mb-2">Titre</label>
                <input
                  data-testid="presave-title"
                  value={presave.title}
                  onChange={(e) => setPresave((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Mon prochain single arrive"
                  className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] block mb-2">Sous-titre</label>
                <input
                  data-testid="presave-subtitle"
                  value={presave.subtitle}
                  onChange={(e) => setPresave((p) => ({ ...p, subtitle: e.target.value }))}
                  placeholder="Sois la première personne à l'écouter le jour J."
                  className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] block mb-2">Lien Spotify (pré-save)</label>
                <input
                  data-testid="presave-spotify"
                  value={presave.spotify_url}
                  onChange={(e) => setPresave((p) => ({ ...p, spotify_url: e.target.value }))}
                  placeholder="https://…"
                  className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] block mb-2">Lien Apple Music (pré-add)</label>
                <input
                  data-testid="presave-apple"
                  value={presave.apple_url}
                  onChange={(e) => setPresave((p) => ({ ...p, apple_url: e.target.value }))}
                  placeholder="https://…"
                  className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] block mb-2">Petite note (facultatif)</label>
                <input
                  data-testid="presave-note"
                  value={presave.note}
                  onChange={(e) => setPresave((p) => ({ ...p, note: e.target.value }))}
                  placeholder="Pré-enregistre-le maintenant ✦"
                  className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
            </div>
            <button
              data-testid="presave-save"
              className="inline-flex items-center gap-2 bg-gold text-black font-medium px-8 py-3.5 hover:bg-gold-light transition-colors text-sm tracking-wide"
            >
              <Save className="w-4 h-4" /> Enregistrer la section
            </button>
          </form>
        </Panel>

        {/* Chat & archive settings */}
        <Panel testid="panel-settings" title="Réglages du Salon" sub="Personnalise ton message de bienvenue et la fréquence d'archivage du chat">
          <form onSubmit={saveSettings} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-2 mb-2">
                <MessageCircle className="w-3.5 h-3.5 text-gold" /> Message de bienvenue AKALIBE
              </label>
              <textarea
                data-testid="settings-welcome"
                rows={3}
                value={settings.welcome_message || ""}
                onChange={(e) => setSettings((s) => ({ ...s, welcome_message: e.target.value }))}
                className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors resize-none"
              />
              <p className="text-[11px] text-[#8A8A8A] mt-2">Astuce : utilise <span className="text-gold">{"{name}"}</span> pour insérer le prénom du fan.</p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-2 mb-2">
                <Video className="w-3.5 h-3.5 text-gold" /> Lien Vidéo Principale (accueil)
              </label>
              <input
                data-testid="settings-video"
                value={settings.featured_video_id || ""}
                onChange={(e) => setSettings((s) => ({ ...s, featured_video_id: e.target.value }))}
                placeholder="Ex : https://youtu.be/VO5VD7LovrM"
                className="w-full bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
              />
              <p className="text-[11px] text-[#8A8A8A] mt-2">Colle le lien YouTube (ou l'ID) de ta vidéo : elle remplace automatiquement le lecteur ET le bouton « Regarder » en page d'accueil.</p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-2 mb-2">
                <Archive className="w-3.5 h-3.5 text-gold" /> Archivage des messages
              </label>
              <select
                data-testid="settings-archive"
                value={settings.archive_days}
                onChange={(e) => setSettings((s) => ({ ...s, archive_days: Number(e.target.value) }))}
                className="w-full sm:w-72 bg-black/40 border border-white/[0.1] px-4 py-3 text-sm text-white focus:outline-none focus:border-gold/50 transition-colors"
              >
                <option value={1}>Quotidien (1 jour)</option>
                <option value={3}>Tous les 3 jours</option>
                <option value={7}>Hebdomadaire (7 jours)</option>
                <option value={14}>Toutes les 2 semaines</option>
                <option value={30}>Mensuel (30 jours)</option>
              </select>
            </div>

            <button
              data-testid="settings-save"
              className="inline-flex items-center gap-2 bg-gold text-black font-medium px-8 py-3.5 hover:bg-gold-light transition-colors text-sm tracking-wide"
            >
              <Save className="w-4 h-4" /> Enregistrer les réglages
            </button>
          </form>
        </Panel>

        {/* Contact / booking messages */}
        <Panel testid="panel-contacts" title="Messages reçus (Contact / Booking)" sub={`${contacts.length} message(s)`}>
          {contacts.length === 0 ? (
            <p className="text-sm text-[#8A8A8A]">Aucun message pour le moment.</p>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {contacts.map((c, i) => (
                <div key={c.id || `${c.email}-${i}`} data-testid={`contact-row-${i}`} className="border border-white/[0.08] bg-white/[0.02] p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-white font-medium">{c.name} · <a href={`mailto:${c.email}`} className="text-gold hover:underline">{c.email}</a></p>
                    <span className="text-xs text-[#8A8A8A]">{(c.created_at || "").slice(0, 10)}</span>
                  </div>
                  {c.subject && <p className="text-xs uppercase tracking-widest text-gold mt-2">{c.subject}</p>}
                  <p className="text-sm text-[#B8B8B8] mt-2 leading-relaxed whitespace-pre-wrap">{c.message}</p>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </main>
    </div>
  );
}
